import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { visibleQuestionIds, type AnswerValue, type SurveyDetail, type SurveySubmission } from 'shared'
import { Alert, Badge, Button, EmptyState, PageHeader, SectionSpinner } from '../../components/ui'
import QuestionList from '../../components/survey/QuestionList'
import { useSurveyForm } from '../../components/survey/useSurveyForm'
import { LOCK_TIMING, useSurveyLock } from '../../components/survey/useSurveyLock'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { cx } from '../../lib/utils'
import { formatDate, formatRelativeTime } from '../../lib/dates'

export default function MemberSurvey() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId, surveyId } = useParams<{ workspaceId: string; surveyId: string }>()

  const [survey, setSurvey] = useState<SurveyDetail | null>(null)
  const [submission, setSubmission] = useState<SurveySubmission | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId || !surveyId) return

    const base = `/api/workspaces/${workspaceId}/surveys/${surveyId}`

    Promise.all([
      api.get<{ data: SurveyDetail }>(base).then((response) => setSurvey(response.data)),
      api
        .get<{ data: SurveySubmission }>(`${base}/submission`)
        .then((response) => setSubmission(response.data)),
    ])
      .catch((err) => setLoadError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId, surveyId])

  if (loading) return <SectionSpinner />

  if (!survey || !submission) {
    return <EmptyState title={t('errors.notFound')} description={loadError ?? undefined} />
  }

  return (
    <SurveyForm
      survey={survey}
      submission={submission}
      workspaceId={workspaceId!}
      onSubmitted={() => navigate(`/w/${workspaceId}`)}
      locale={i18n.language}
    />
  )
}

interface SurveyFormProps {
  survey: SurveyDetail
  submission: SurveySubmission
  workspaceId: string
  onSubmitted: () => void
  locale: string
}

function SurveyForm({ survey, submission, workspaceId, onSubmitted, locale }: SurveyFormProps) {
  const { t } = useTranslation()
  const alreadySubmitted = submission.status === 'submitted'
  const closed = survey.status === 'closed'
  const individual = survey.response_mode === 'individual'
  const anonymous = survey.anonymous
  const readOnly = alreadySubmitted || closed

  const initial: Record<string, AnswerValue> = Object.fromEntries(
    submission.answers.map((answer) => [answer.question_id, answer.value]),
  )

  // The company's answer is one, so one person at a time works on it. An individual
  // answer is a person's own and an anonymous one is written whole at the end:
  // neither has anyone to wait for.
  const collective = !individual && !anonymous
  const [lostEdits, setLostEdits] = useState(false)
  const refreshRef = useRef<(() => void) | undefined>(undefined)

  const lock = useSurveyLock({
    workspaceId,
    surveyId: survey.id,
    enabled: collective && !readOnly,
    onFreed: () => refreshRef.current?.(),
  })

  const form = useSurveyForm({
    workspaceId,
    surveyId: survey.id,
    questions: survey.questions,
    initialAnswers: initial,
    submitted: readOnly,
    anonymous,
    onLocked: (holder, lost) => {
      lock.markLocked(holder)
      if (lost) setLostEdits(true)
    },
  })

  const lockedOut = lock.status === 'other'

  // What someone else wrote while this person was only reading: taken as it is on the
  // server, now and then while locked out, and once more when the lock is let go.
  const refresh = useCallback(async () => {
    try {
      const response = await api.get<{ data: SurveySubmission }>(
        `/api/workspaces/${workspaceId}/surveys/${survey.id}/submission`,
      )
      form.replaceAnswers(
        Object.fromEntries(response.data.answers.map((answer) => [answer.question_id, answer.value])),
      )
    } catch {
      // Shown again at the next tick.
    }
  }, [workspaceId, survey.id, form.replaceAnswers]) // eslint-disable-line react-hooks/exhaustive-deps
  refreshRef.current = () => void refresh()

  useEffect(() => {
    if (!lockedOut) return
    void refresh()
    const timer = setInterval(() => void refresh(), LOCK_TIMING.pollMs)
    return () => clearInterval(timer)
  }, [lockedOut, refresh])

  // Back to working after being locked out: the notice about lost changes has done its job.
  useEffect(() => {
    if (lock.status === 'mine') setLostEdits(false)
  }, [lock.status])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (await form.submit()) onSubmitted()
  }

  // Counted against what is asked: a question that is not shown is neither owed nor answered.
  const asked = visibleQuestionIds(survey.questions, form.answers)
  const owed = survey.questions.filter(
    (question) => question.type !== 'section' && asked.has(question.id),
  )
  const answered = owed.filter((question) => !isBlank(form.answers[question.id] ?? null)).length

  // A form meant to be filled over several sittings needs a readable measure
  // and a sense of how far along it is; at full page width it is neither.
  return (
    <div
      className={cx(
        'mx-auto',
        // A table is wide by nature: it gets the room, a form of short answers does not need it.
        survey.questions.some((question) => question.type === 'table') ? 'max-w-6xl' : 'max-w-3xl',
      )}
    >
      <PageHeader
        backTo={{ href: `/w/${workspaceId}`, label: t('timeline.title') }}
        title={survey.title}
        subtitle={survey.description ?? undefined}
      />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        {alreadySubmitted && <Badge variant="success">{t('survey.submitted')}</Badge>}
        {closed && !alreadySubmitted && <Badge variant="neutral">{t('survey.closed')}</Badge>}
        {!readOnly && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('survey.progress', { answered, total: owed.length })}
          </span>
        )}
        {survey.due_at && !readOnly && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('survey.dueOn', { date: formatDate(survey.due_at, locale) })}
          </span>
        )}
      </div>

      {readOnly && (
        <Alert variant="info" className="mb-6">
          {alreadySubmitted
            ? t(
                anonymous
                  ? 'survey.submittedNoteAnonymous'
                  : individual
                    ? 'survey.submittedNoteIndividual'
                    : 'survey.submittedNote',
              )
            : t('survey.closedNote')}
        </Alert>
      )}

      {individual && !readOnly && (
        <Alert variant="info" className="mb-6">
          {t(anonymous ? 'survey.anonymousNote' : 'survey.individualNote')}
        </Alert>
      )}

      {lockedOut && (
        <Alert variant="warning" className="mb-6">
          {lock.holder
            ? t('survey.lock.other', { name: lock.holder.name })
            : t('survey.lock.otherUnknown')}{' '}
          {t('survey.lock.readOnly')}
          {lostEdits && <> {t('survey.lock.lost')}</>}
        </Alert>
      )}

      <form
        onSubmit={handleSubmit}
        // Working starts at the first touch, not when the page opens: reading never
        // keeps anybody out. The server is what refuses a second writer.
        onFocusCapture={() => !readOnly && !lockedOut && void lock.acquire()}
        onChangeCapture={() => !readOnly && !lockedOut && void lock.acquire()}
        onKeyDownCapture={lock.touch}
        onPointerDownCapture={lock.touch}
      >
        <QuestionList
          questions={survey.questions}
          answers={form.answers}
          onChange={form.setAnswer}
          errors={form.errors}
          disabled={readOnly || lockedOut}
          readOnly={readOnly || lockedOut}
        />

        {form.error && <Alert className="mt-6">{form.error}</Alert>}

        {!readOnly && !lockedOut && (
          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
              <p>
                {anonymous
                  ? t('survey.anonymousSaveHint')
                  : form.saving
                    ? t('survey.saving')
                    : form.savedAt
                      ? t('survey.savedAt', { time: formatRelativeTime(form.savedAt, locale) })
                      : t('survey.autosaveHint')}
              </p>
              {lock.status === 'mine' && <p className="mt-0.5">{t('survey.lock.mine')}</p>}
            </div>
            <Button type="submit" variant="cta" loading={form.submitting}>
              {t('survey.submit')}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}

/** Mirrors the validator's idea of "no answer" — `false` and `0` are answers. */
function isBlank(value: AnswerValue): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}
